/**
 * PersonaTopologyView (DC-1648).
 *
 * Force-directed governance topology over the per-org FAIR `personas`
 * catalog — the successor to the retired standalone GovernanceTopology
 * page. Rendered inside FairPersonasAdmin behind the Catalog | Topology
 * toggle (?view=topology).
 *
 * D3 lifecycle rework (the DC-1648 perf fix). The legacy component did a
 * full `selectAll('*').remove()` rebuild + a fresh force simulation on
 * EVERY state change. This version splits the work into three effects:
 *
 *   1. Mount-once scaffolding — svg groups, zoom behavior, and ONE
 *      persistent forceSimulation (configured, stopped) held in a ref.
 *   2. Data effect — keyed join (`.data(nodes, d => d.id).join(...)`) so
 *      unchanged nodes keep their DOM; node positions are seeded from a
 *      position cache (positionsRef) that survives filter changes, and the
 *      simulation does a gentle `alpha(0.3)` reheat from those cached
 *      positions instead of a from-scratch re-layout (full alpha only on
 *      the very first render).
 *   3. Decoration effects — focus ring (DC-189) and assigned-state classes
 *      toggle CSS classes on the existing DOM; they never rebuild it.
 *
 * Deleted machinery (30-40 nodes doesn't need it): macro/meso/micro view
 * tiers, the Auto/Normal/Fast performance toggle, tick-skip, the
 * GraphPerformanceMonitor wiring, and the dead `recommendations` badge
 * prop (no caller ever passed it).
 *
 * Kept: drag-to-assign + EmployeeSidebar (DC-182), keyboard navigation
 * (DC-189), stats bar, orphan/bus-factor alert bars (DC-186/187),
 * AlertHistoryPanel (DC-194), role filter + baseline checkbox.
 *
 * All colors are CSS-class driven (persona-topology.css, var(--tri-*)) —
 * no hex from JS; SVG fills resolve var() through CSS rules, which
 * `.attr()` would not.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import * as d3 from 'd3';
import { useApiClient } from '../../../hooks/useApiClient';
import type { Persona } from '../../../types/triangulation';
import { PRIMARY_ROLES } from '../../../types/triangulation';
import type {
  Assignment,
  TopologyEmployee,
  EmployeeAssignmentInfo,
  TopologyNode,
  TopologyLink,
} from './types';
import { KEYS, ROLE_POSITIONS, BUS_FACTOR_THRESHOLD, roleClassOf } from './types';
import { buildGraph } from './personaGraphAdapter';
import { EmployeeSidebar } from './EmployeeSidebar';
import { AssignmentModal } from './AssignmentModal';
import { TopologyLegend } from './TopologyLegend';
import { AlertHistoryPanel } from './AlertHistoryPanel';
import './persona-topology.css';

const NODE_RADIUS = 26;

// The legacy org-level topology key — assignments made from this surface are
// org-scoped, not journey-scoped (server resolves the org via its fallback
// ladder; see migration 112 / governanceRepository.getOrgIdForJourney).
const ORG_TOPOLOGY_JOURNEY_KEY = '00000000-0000-0000-0000-000000000000';

interface PersonaTopologyViewProps {
  /** The org's personas — fetched once by FairPersonasAdmin and shared. */
  personas: Persona[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  /** Flip the FairPersonasAdmin toggle back to the catalog view. */
  onGoToCatalog: () => void;
  /**
   * Renders the shared persona detail content (FairPersonasAdmin's panel) —
   * passed as a render prop so the two views share ONE panel without a
   * circular import.
   */
  renderPersonaDetail: (persona: Persona) => React.ReactNode;
}

export const PersonaTopologyView: React.FC<PersonaTopologyViewProps> = ({
  personas,
  loading,
  error,
  onRetry,
  onGoToCatalog,
  renderPersonaDetail,
}) => {
  const { authFetch } = useApiClient();

  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [selectedPersona, setSelectedPersona] = useState<Persona | null>(null);
  const [filterRole, setFilterRole] = useState<string>('all');
  const [showBaseline, setShowBaseline] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [personaToAssign, setPersonaToAssign] = useState<Persona | null>(null);

  // DC-182: Drag-and-drop state
  const [employees, setEmployees] = useState<TopologyEmployee[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState(true);
  const [draggedEmployee, setDraggedEmployee] = useState<TopologyEmployee | null>(null);
  const [dropFeedback, setDropFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const draggedEmployeeRef = useRef<TopologyEmployee | null>(null);

  // DC-189: Accessibility - keyboard navigation state
  const [focusedNodeId, setFocusedNodeId] = useState<string | null>(null);
  const [isGraphFocused, setIsGraphFocused] = useState(false);
  const [announceMessage, setAnnounceMessage] = useState<string>('');

  // DC-194: Alert history panel state
  const [alertPanelOpen, setAlertPanelOpen] = useState(false);

  // Refs — D3 scaffolding that persists across renders
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const simulationRef = useRef<d3.Simulation<TopologyNode, TopologyLink> | null>(null);
  const linkForceRef = useRef<d3.ForceLink<TopologyNode, TopologyLink> | null>(null);
  const nodesGRef = useRef<d3.Selection<SVGGElement, unknown, null, undefined> | null>(null);
  const linksGRef = useRef<d3.Selection<SVGGElement, unknown, null, undefined> | null>(null);
  const nodeSelRef = useRef<d3.Selection<SVGGElement, TopologyNode, SVGGElement, unknown> | null>(null);
  const linkSelRef = useRef<d3.Selection<SVGLineElement, TopologyLink, SVGGElement, unknown> | null>(null);
  /** Converged node positions, surviving filter changes and re-fetches. */
  const positionsRef = useRef<Map<string, { x: number; y: number }>>(new Map());
  const graphNodesRef = useRef<TopologyNode[]>([]);
  const firstRenderRef = useRef(true);

  // ---------------------------------------------------------------------------
  // Data fetching (assignments + employees; personas come from the parent)
  // ---------------------------------------------------------------------------

  const fetchAssignments = useCallback(async () => {
    try {
      const res = await authFetch('/api/governance/assignments');
      if (res.ok) {
        const data = await res.json();
        setAssignments(data.assignments || []);
      }
    } catch (err) {
      console.error('[PersonaTopology] Failed to fetch assignments:', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- authFetch is stable from useApiClient
  }, []);

  useEffect(() => {
    void fetchAssignments();
  }, [fetchAssignments]);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        setEmployeesLoading(true);
        const res = await authFetch('/api/employees');
        const data = await res.json();
        const activeEmployees = (data.employees || []).filter((e: TopologyEmployee) => e.is_active);
        setEmployees(activeEmployees);
      } catch (err) {
        console.error('[PersonaTopology] Failed to fetch employees:', err);
      } finally {
        setEmployeesLoading(false);
      }
    };
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- authFetch is stable from useApiClient
  }, []);

  // ---------------------------------------------------------------------------
  // Computed data
  // ---------------------------------------------------------------------------

  const assignmentMap = useMemo(() => {
    return new Map(assignments.map((a) => [a.persona_id, a]));
  }, [assignments]);

  const personaById = useMemo(() => new Map(personas.map((p) => [p.id, p])), [personas]);

  const employeeAssignments = useMemo(() => {
    const map = new Map<string, EmployeeAssignmentInfo>();
    assignments.forEach((a) => {
      const persona = personaById.get(a.persona_id);
      const assignmentDetail = {
        persona_id: a.persona_id,
        persona_name: persona?.name || 'Unknown',
        primary_role: persona?.primary_role || 'unknown',
      };

      const existing = map.get(a.employee_id);
      if (existing) {
        existing.assignments.push(assignmentDetail);
      } else {
        map.set(a.employee_id, {
          employee_id: a.employee_id,
          assignments: [assignmentDetail],
        });
      }
    });
    return map;
  }, [assignments, personaById]);

  const orphanedPersonas = useMemo(
    () => personas.filter((p) => p.is_baseline && !assignmentMap.has(p.id)),
    [personas, assignmentMap],
  );

  const busFactorWarnings = useMemo(() => {
    const counts = new Map<string, { name: string; count: number; roles: string[] }>();
    assignments.forEach((a) => {
      const persona = personaById.get(a.persona_id);
      const roleName = persona?.name || 'Unknown';
      const existing = counts.get(a.employee_id);
      if (existing) {
        existing.count++;
        existing.roles.push(roleName);
      } else {
        counts.set(a.employee_id, { name: a.employee_name, count: 1, roles: [roleName] });
      }
    });
    return Array.from(counts.entries())
      .filter(([, data]) => data.count > BUS_FACTOR_THRESHOLD)
      .map(([employeeId, data]) => ({
        employeeId,
        employeeName: data.name,
        roleCount: data.count,
        roles: data.roles,
      }));
  }, [assignments, personaById]);

  const filteredPersonas = useMemo(() => {
    return personas.filter((p) => {
      if (showBaseline && !p.is_baseline) return false;
      if (filterRole !== 'all' && p.primary_role !== filterRole) return false;
      return true;
    });
  }, [personas, showBaseline, filterRole]);

  const graph = useMemo(() => buildGraph(filteredPersonas), [filteredPersonas]);

  const stats = useMemo(() => {
    const uniqueEmployeesAssigned = new Set(assignments.map(a => a.employee_id)).size;
    const uniquePersonasAssigned = new Set(assignments.map(a => a.persona_id)).size;

    return {
      total: personas.length,
      baseline: personas.filter((p) => p.is_baseline).length,
      employeesAssigned: uniqueEmployeesAssigned,
      personasAssigned: uniquePersonasAssigned,
      personasUnassigned: Math.max(0, personas.length - uniquePersonasAssigned),
    };
  }, [personas, assignments]);

  // ---------------------------------------------------------------------------
  // Drag-to-assign handlers (DC-182)
  // ---------------------------------------------------------------------------

  const handleEmployeeDragStart = useCallback((employee: TopologyEmployee, event: React.DragEvent) => {
    draggedEmployeeRef.current = employee;
    setDraggedEmployee(employee);
    event.dataTransfer.setData('text/plain', employee.employee_id);
    event.dataTransfer.effectAllowed = 'copy';
  }, []);

  const handleDragEnd = useCallback(() => {
    draggedEmployeeRef.current = null;
    setDraggedEmployee(null);
  }, []);

  const personasRef = useRef<Persona[]>([]);
  personasRef.current = personas;
  const assignmentsRef = useRef<Assignment[]>([]);
  assignmentsRef.current = assignments;

  const handleDropOnPersona = useCallback(async (personaId: string) => {
    const employee = draggedEmployeeRef.current;
    if (!employee) return;

    const persona = personasRef.current.find((p) => p.id === personaId);
    if (!persona) return;

    const existingAssignment = assignmentsRef.current.find(
      (a) => a.employee_id === employee.employee_id && a.persona_id === personaId
    );

    if (existingAssignment) {
      setDropFeedback({
        type: 'error',
        message: `${employee.display_name} is already assigned as ${persona.name}`,
      });
      setTimeout(() => setDropFeedback(null), 3000);
      draggedEmployeeRef.current = null;
      setDraggedEmployee(null);
      return;
    }

    try {
      const response = await authFetch('/api/governance/assignments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          persona_id: personaId,
          employee_id: employee.employee_id,
          journey_id: ORG_TOPOLOGY_JOURNEY_KEY,
          scope: 'organization',
          assignment_notes: `Assigned via drag-and-drop`,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to create assignment');
      }

      setDropFeedback({
        type: 'success',
        message: `${employee.display_name} assigned as ${persona.name}`,
      });
      void fetchAssignments();
      setTimeout(() => setDropFeedback(null), 3000);
    } catch (err: unknown) {
      console.error('[PersonaTopology] Drop assignment error:', err);
      setDropFeedback({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to assign',
      });
      setTimeout(() => setDropFeedback(null), 3000);
    } finally {
      draggedEmployeeRef.current = null;
      setDraggedEmployee(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- authFetch is stable from useApiClient
  }, [fetchAssignments]);

  const handleDropOnPersonaRef = useRef(handleDropOnPersona);
  handleDropOnPersonaRef.current = handleDropOnPersona;

  const handleUnassign = useCallback(async (employeeId: string, personaId: string, personaName: string) => {
    const assignment = assignments.find(
      (a) => a.employee_id === employeeId && a.persona_id === personaId
    );

    if (!assignment) {
      setDropFeedback({ type: 'error', message: 'Assignment not found' });
      setTimeout(() => setDropFeedback(null), 3000);
      return;
    }

    try {
      const response = await authFetch(`/api/governance/assignments/${assignment.assignment_id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to remove assignment');
      }

      setDropFeedback({ type: 'success', message: `Removed ${personaName} assignment` });
      void fetchAssignments();
      setTimeout(() => setDropFeedback(null), 3000);
    } catch (err: unknown) {
      console.error('[PersonaTopology] Unassign error:', err);
      setDropFeedback({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to remove assignment',
      });
      setTimeout(() => setDropFeedback(null), 3000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- authFetch is stable from useApiClient
  }, [assignments, fetchAssignments]);

  // ---------------------------------------------------------------------------
  // DC-189: keyboard navigation
  // ---------------------------------------------------------------------------

  const announce = useCallback((message: string) => {
    setAnnounceMessage(message);
    setTimeout(() => setAnnounceMessage(''), 1000);
  }, []);

  const findNearestNode = useCallback((
    currentNode: TopologyNode,
    direction: 'up' | 'down' | 'left' | 'right',
    nodes: TopologyNode[]
  ): TopologyNode | null => {
    if (!currentNode.x || !currentNode.y) return null;

    const candidates = nodes.filter((n) => {
      if (n.id === currentNode.id || !n.x || !n.y) return false;
      const dx = n.x - currentNode.x!;
      const dy = n.y - currentNode.y!;

      switch (direction) {
        case 'up': return dy < -20;
        case 'down': return dy > 20;
        case 'left': return dx < -20;
        case 'right': return dx > 20;
        default: return false;
      }
    });

    if (candidates.length === 0) return null;

    return candidates.reduce((nearest, node) => {
      const distToNearest = Math.hypot(
        (nearest.x || 0) - currentNode.x!,
        (nearest.y || 0) - currentNode.y!
      );
      const distToNode = Math.hypot(
        (node.x || 0) - currentNode.x!,
        (node.y || 0) - currentNode.y!
      );
      return distToNode < distToNearest ? node : nearest;
    }, candidates[0]);
  }, []);

  const handleGraphKeyDown = useCallback((event: React.KeyboardEvent) => {
    const nodes = graphNodesRef.current;
    if (nodes.length === 0) return;

    if (event.key === KEYS.ESCAPE) {
      if (selectedPersona) {
        setSelectedPersona(null);
        announce('Detail panel closed');
        event.preventDefault();
        return;
      }
      setIsGraphFocused(false);
      setFocusedNodeId(null);
      announce('Exited graph navigation');
      event.preventDefault();
      return;
    }

    if (!focusedNodeId && ([KEYS.ARROW_UP, KEYS.ARROW_DOWN, KEYS.ARROW_LEFT, KEYS.ARROW_RIGHT] as string[]).includes(event.key)) {
      const firstNode = nodes[0];
      setFocusedNodeId(firstNode.id);
      announce(`Focused on ${firstNode.persona.name}. Use arrow keys to navigate.`);
      event.preventDefault();
      return;
    }

    if (event.key === KEYS.HOME) {
      const firstNode = nodes[0];
      setFocusedNodeId(firstNode.id);
      announce(`${firstNode.persona.name}, first persona`);
      event.preventDefault();
      return;
    }

    if (event.key === KEYS.END) {
      const lastNode = nodes[nodes.length - 1];
      setFocusedNodeId(lastNode.id);
      announce(`${lastNode.persona.name}, last persona`);
      event.preventDefault();
      return;
    }

    if (focusedNodeId) {
      const currentNode = nodes.find((n) => n.id === focusedNodeId);
      if (!currentNode) return;

      let direction: 'up' | 'down' | 'left' | 'right' | null = null;
      switch (event.key) {
        case KEYS.ARROW_UP: direction = 'up'; break;
        case KEYS.ARROW_DOWN: direction = 'down'; break;
        case KEYS.ARROW_LEFT: direction = 'left'; break;
        case KEYS.ARROW_RIGHT: direction = 'right'; break;
      }

      if (direction) {
        const nextNode = findNearestNode(currentNode, direction, nodes);
        if (nextNode) {
          setFocusedNodeId(nextNode.id);
          const roleLabel = nextNode.persona.primary_role || 'Unknown role';
          const statusLabels = [
            nextNode.isBaseline && 'baseline',
            nextNode.isCritical && 'critical',
          ].filter(Boolean).join(', ');
          announce(
            `${nextNode.persona.name}, ${roleLabel}${statusLabels ? `, ${statusLabels}` : ''}`
          );
        } else {
          announce(`No more personas in that direction`);
        }
        event.preventDefault();
        return;
      }

      if (event.key === KEYS.ENTER || event.key === KEYS.SPACE) {
        setSelectedPersona(currentNode.persona);
        announce(`Selected ${currentNode.persona.name}. Press Escape to close.`);
        event.preventDefault();
        return;
      }
    }
  }, [focusedNodeId, selectedPersona, findNearestNode, announce]);

  const handleGraphFocus = useCallback(() => {
    setIsGraphFocused(true);
    if (!focusedNodeId && graphNodesRef.current.length > 0) {
      announce('Graph focused. Use arrow keys to navigate between personas, Enter to select.');
    }
  }, [focusedNodeId, announce]);

  const handleGraphBlur = useCallback(() => {
    setTimeout(() => {
      if (!containerRef.current?.contains(document.activeElement)) {
        setIsGraphFocused(false);
      }
    }, 0);
  }, []);

  // The graph surface only exists in the DOM once personas have loaded —
  // the scaffolding effect keys on this so a deep link into ?view=topology
  // (component mounts in its loading state, svgRef still null) builds the
  // simulation when the svg actually appears, not never.
  const showGraph = !loading && !error && personas.length > 0;

  // ---------------------------------------------------------------------------
  // D3 effect 1 — scaffolding + persistent simulation (runs when the graph
  // surface enters the DOM; torn down when it leaves)
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!showGraph) return;
    if (!svgRef.current || !containerRef.current) return;

    const svg = d3.select(svgRef.current);
    const g = svg.append('g').attr('class', 'graph-root');
    linksGRef.current = g.append('g').attr('class', 'links') as unknown as d3.Selection<SVGGElement, unknown, null, undefined>;
    nodesGRef.current = g.append('g').attr('class', 'nodes') as unknown as d3.Selection<SVGGElement, unknown, null, undefined>;

    svg.call(
      d3.zoom<SVGSVGElement, unknown>()
        .scaleExtent([0.3, 3])
        .on('zoom', (event: d3.D3ZoomEvent<SVGSVGElement, unknown>) => {
          g.attr('transform', event.transform.toString());
        }) as unknown as (selection: d3.Selection<SVGSVGElement, unknown, null, undefined>) => void
    );

    const linkForce = d3.forceLink<TopologyNode, TopologyLink>([])
      .id((d) => d.id)
      .distance(120)
      .strength(0.3);
    linkForceRef.current = linkForce;

    const container = containerRef.current;
    const width = container.clientWidth || 960;
    const height = Math.max(500, container.clientHeight);

    const simulation = d3.forceSimulation<TopologyNode>([])
      .force('link', linkForce)
      .force('charge', d3.forceManyBody().strength(-400))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius(48))
      .force('x', d3.forceX<TopologyNode>().x((d) => {
        const pos = ROLE_POSITIONS[d.role];
        return pos ? pos.x * width : width / 2;
      }).strength(0.1))
      .force('y', d3.forceY<TopologyNode>().y((d) => {
        const pos = ROLE_POSITIONS[d.role];
        return pos ? pos.y * height : height / 2;
      }).strength(0.1))
      // Calmer, faster settle than the d3 defaults — 30-40 nodes packed on
      // three role anchors visibly vibrate while collisions resolve; higher
      // decay damps the oscillation instead of letting it ring for seconds.
      .alphaDecay(0.05)
      .velocityDecay(0.5);

    simulation.on('tick', () => {
      linkSelRef.current
        ?.attr('x1', (d) => (d as unknown as { source: { x: number } }).source.x)
        .attr('y1', (d) => (d as unknown as { source: { y: number } }).source.y)
        .attr('x2', (d) => (d as unknown as { target: { x: number } }).target.x)
        .attr('y2', (d) => (d as unknown as { target: { y: number } }).target.y);

      nodeSelRef.current?.attr('transform', (d) => `translate(${d.x ?? 0},${d.y ?? 0})`);
    });

    // Persist converged positions so filter changes settle gently instead
    // of re-running the whole layout.
    simulation.on('end', () => {
      for (const n of simulation.nodes()) {
        if (n.x !== undefined && n.y !== undefined) {
          positionsRef.current.set(n.id, { x: n.x, y: n.y });
        }
      }
    });

    simulation.stop();
    simulationRef.current = simulation;
    // Fresh scaffold → the next data join is a first layout (full alpha).
    firstRenderRef.current = true;

    return () => {
      simulation.stop();
      simulationRef.current = null;
      nodesGRef.current = null;
      linksGRef.current = null;
      nodeSelRef.current = null;
      linkSelRef.current = null;
      svg.selectAll('*').remove();
    };
  }, [showGraph]);

  // ---------------------------------------------------------------------------
  // D3 effect 2 — keyed data join + gentle reheat
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const simulation = simulationRef.current;
    const nodesG = nodesGRef.current;
    const linksG = linksGRef.current;
    const container = containerRef.current;
    if (!simulation || !nodesG || !linksG || !container || !svgRef.current) return;
    if (graph.nodes.length === 0) {
      // Clear any prior render so an emptied filter doesn't strand stale DOM.
      nodesG.selectAll('*').remove();
      linksG.selectAll('*').remove();
      nodeSelRef.current = null;
      linkSelRef.current = null;
      graphNodesRef.current = [];
      simulation.nodes([]);
      simulation.stop();
      return;
    }

    const width = container.clientWidth || 960;
    const height = Math.max(500, container.clientHeight);

    d3.select(svgRef.current)
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', [0, 0, width, height].join(' '));

    // Stable handle for the position cache — also used by the effect
    // cleanup, where reading the ref directly would trip
    // react-hooks/exhaustive-deps.
    const positions = positionsRef.current;

    // Seed positions: cached convergence first, role anchor + jitter for
    // genuinely new nodes (no fly-in from the origin).
    const jitter = (i: number) => ((i % 7) - 3) * 14;
    graph.nodes.forEach((n, i) => {
      const cached = positions.get(n.id);
      if (cached) {
        n.x = cached.x;
        n.y = cached.y;
      } else {
        const pos = ROLE_POSITIONS[n.role];
        n.x = (pos ? pos.x * width : width / 2) + jitter(i);
        n.y = (pos ? pos.y * height : height / 2) + jitter(i * 3);
      }
    });

    graphNodesRef.current = graph.nodes;

    // Keyed joins — unchanged nodes keep their DOM.
    const linkSel = (linksG.selectAll('line.pt-link') as d3.Selection<SVGLineElement, TopologyLink, SVGGElement, unknown>)
      .data(graph.links, (d) => {
        const s = typeof d.source === 'string' ? d.source : d.source.id;
        const t = typeof d.target === 'string' ? d.target : d.target.id;
        return [s, t].sort().join('|');
      })
      .join(
        (enter) => enter.append('line').attr('class', 'pt-link'),
        (update) => update,
        (exit) => { exit.remove(); return exit; },
      );
    linkSelRef.current = linkSel;

    const nodeSel = (nodesG.selectAll('g.pt-node') as d3.Selection<SVGGElement, TopologyNode, SVGGElement, unknown>)
      .data(graph.nodes, (d) => d.id)
      .join(
        (enter) => {
          const gEnter = enter.append('g');

          // DC-182: oversized transparent drop target for drag-assign.
          gEnter.append('circle')
            .attr('class', 'drop-target')
            .attr('r', NODE_RADIUS + 14)
            .style('pointer-events', 'all');

          gEnter.append('circle')
            .attr('class', 'node-ring')
            .attr('r', NODE_RADIUS + 6);

          gEnter.append('circle')
            .attr('class', 'node-circle')
            .attr('r', NODE_RADIUS);

          gEnter.append('circle')
            .attr('class', 'focus-ring')
            .attr('r', NODE_RADIUS + 10)
            .attr('opacity', 0);

          gEnter.append('circle')
            .attr('class', 'assigned-dot')
            .attr('cx', NODE_RADIUS - 6)
            .attr('cy', NODE_RADIUS - 6)
            .attr('r', 5);

          gEnter.append('text')
            .attr('class', 'node-initials')
            .attr('text-anchor', 'middle')
            .attr('dy', '0.35em')
            .attr('pointer-events', 'none');

          gEnter.append('text')
            .attr('class', 'node-label')
            .attr('text-anchor', 'middle')
            .attr('dy', '48px')
            .attr('pointer-events', 'none');

          gEnter.append('title');

          gEnter.each(function (d) {
            const element = this as SVGGElement;
            element.addEventListener('dragover', (e) => {
              e.preventDefault();
              e.stopPropagation();
              d3.select(element).classed('drop-hover', true);
            });
            element.addEventListener('dragleave', (e) => {
              e.preventDefault();
              e.stopPropagation();
              d3.select(element).classed('drop-hover', false);
            });
            element.addEventListener('drop', (e) => {
              e.preventDefault();
              e.stopPropagation();
              d3.select(element).classed('drop-hover', false);
              handleDropOnPersonaRef.current(d.id);
            });
          });

          return gEnter;
        },
        (update) => update,
        (exit) => {
          // Remember where exiting nodes were so a filter round-trip
          // restores them in place.
          exit.each((d) => {
            if (d.x !== undefined && d.y !== undefined) {
              positionsRef.current.set(d.id, { x: d.x, y: d.y });
            }
          });
          exit.remove();
          return exit;
        },
      );

    // Attributes that depend on data — set on enter+update (cheap, no DOM
    // rebuild).
    nodeSel
      .attr('class', (d) =>
        [
          'pt-node',
          roleClassOf(d.persona.primary_role),
          d.isBaseline ? 'baseline' : '',
          d.isCritical ? 'critical' : '',
        ].filter(Boolean).join(' '),
      )
      .style('cursor', 'pointer')
      .on('click', (_event, d) => { setSelectedPersona(d.persona); });

    nodeSel.select<SVGTextElement>('text.node-initials').text((d) => {
      const words = d.persona.name.split(/[\s-]+/);
      if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
      return words.slice(0, 2).map((w) => w[0]).join('').toUpperCase();
    });

    nodeSel.select<SVGTextElement>('text.node-label').text((d) =>
      d.persona.name.length <= 16 ? d.persona.name : d.persona.name.slice(0, 15) + '...',
    );

    nodeSel.select<SVGTitleElement>('title').text((d) => {
      const desc = d.persona.description ?? '';
      return `${d.persona.name}\n${d.persona.primary_role ?? 'Unassigned role'}${desc ? `\n${desc.slice(0, 100)}…` : ''}`;
    });

    // Drag behavior (re-bound each join so it captures the live simulation).
    const dragBehavior = d3.drag<SVGGElement, TopologyNode>()
      .on('start', (event: d3.D3DragEvent<SVGGElement, TopologyNode, TopologyNode>) => {
        if (!event.active) simulation.alphaTarget(0.3).restart();
        event.subject.fx = event.subject.x;
        event.subject.fy = event.subject.y;
      })
      .on('drag', (event: d3.D3DragEvent<SVGGElement, TopologyNode, TopologyNode>) => {
        event.subject.fx = event.x;
        event.subject.fy = event.y;
      })
      .on('end', (event: d3.D3DragEvent<SVGGElement, TopologyNode, TopologyNode>) => {
        if (!event.active) simulation.alphaTarget(0);
        if (event.subject.x !== undefined && event.subject.y !== undefined) {
          positionsRef.current.set(event.subject.id, { x: event.subject.x, y: event.subject.y });
        }
        event.subject.fx = null;
        event.subject.fy = null;
      });
    nodeSel.call(dragBehavior as unknown as (selection: d3.Selection<SVGGElement, TopologyNode, SVGGElement, unknown>) => void);

    nodeSelRef.current = nodeSel;

    // Feed the persistent simulation; gentle reheat from cached positions —
    // full alpha only on the very first layout.
    simulation.nodes(graph.nodes);
    linkForceRef.current?.links(graph.links);
    simulation.alpha(firstRenderRef.current ? 1 : 0.3).restart();
    firstRenderRef.current = false;

    return () => {
      // Capture latest positions on data change so the next join seeds from
      // the freshest layout even if the simulation never reached 'end'.
      for (const n of simulation.nodes()) {
        if (n.x !== undefined && n.y !== undefined) {
          positions.set(n.id, { x: n.x, y: n.y });
        }
      }
    };
  }, [graph]);

  // ---------------------------------------------------------------------------
  // D3 effect 3a — focus ring decoration (DC-189)
  // ---------------------------------------------------------------------------

  useEffect(() => {
    nodeSelRef.current
      ?.select<SVGCircleElement>('circle.focus-ring')
      .attr('opacity', (d) => (d.id === focusedNodeId && isGraphFocused ? 1 : 0));
  }, [focusedNodeId, isGraphFocused, graph]);

  // ---------------------------------------------------------------------------
  // D3 effect 3b — assigned-state decoration (no rebuild on assignment change)
  // ---------------------------------------------------------------------------

  useEffect(() => {
    nodeSelRef.current?.classed('assigned', (d) => assignmentMap.has(d.id));
  }, [assignmentMap, graph]);

  // ---------------------------------------------------------------------------
  // Assignment modal
  // ---------------------------------------------------------------------------

  const handleAssignClick = (persona: Persona) => {
    setPersonaToAssign(persona);
    setAssignModalOpen(true);
  };

  const handleAssign = async (personaId: string, employeeId: string, notes: string) => {
    const response = await authFetch('/api/governance/assignments', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        persona_id: personaId,
        employee_id: employeeId,
        journey_id: ORG_TOPOLOGY_JOURNEY_KEY,
        scope: 'organization',
        assignment_notes: notes,
      }),
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.message || 'Failed to create assignment');
    }

    void fetchAssignments();
  };

  const roles = ['all', ...PRIMARY_ROLES.filter((r) => personas.some((p) => p.primary_role === r))];

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="persona-topology" data-testid="persona-topology-view">
      {/* Stats Bar */}
      <div className="stats-bar">
        <div className="stat-item">
          <span className="stat-value">{stats.total}</span>
          <span className="stat-label">Total Personas</span>
        </div>
        <div className="stat-item">
          <span className="stat-value good">{stats.employeesAssigned}</span>
          <span className="stat-label">{stats.employeesAssigned === 1 ? 'Employee' : 'Employees'} Assigned</span>
        </div>
        <div className="stat-item">
          <span className="stat-value teal">{stats.personasAssigned}</span>
          <span className="stat-label">{stats.personasAssigned === 1 ? 'Persona' : 'Personas'} Covered</span>
        </div>
        <div className="stat-item">
          <span className={`stat-value ${stats.personasUnassigned > 0 ? 'soft' : 'good'}`}>{stats.personasUnassigned}</span>
          <span className="stat-label">Unassigned</span>
        </div>
        <div className="stat-item">
          <span className="stat-value">{stats.baseline}</span>
          <span className="stat-label">Baseline Required</span>
        </div>
        <button
          className="pt-btn secondary alert-history-btn"
          onClick={() => setAlertPanelOpen(true)}
          aria-label="View alert history"
        >
          Alert History
        </button>
      </div>

      {/* Orphan Alert Bar (DC-186) */}
      {orphanedPersonas.length > 0 && (
        <div className="alert-bar alert-orphan" data-testid="topology-orphan-alert">
          <div className="alert-content">
            <strong>{orphanedPersonas.length} baseline role{orphanedPersonas.length > 1 ? 's' : ''} unassigned</strong>
            <p>
              The following critical roles need team member assignments:{' '}
              {orphanedPersonas.slice(0, 3).map((p) => p.name).join(', ')}
              {orphanedPersonas.length > 3 && ` and ${orphanedPersonas.length - 3} more`}
            </p>
          </div>
          <button className="pt-btn warning" onClick={() => { setShowBaseline(true); setFilterRole('all'); }}>
            View Unassigned
          </button>
        </div>
      )}

      {/* Bus Factor Warning (DC-187) */}
      {busFactorWarnings.length > 0 && (
        <div className="alert-bar alert-bus-factor" data-testid="topology-bus-factor-alert">
          <div className="alert-content">
            <strong>Concentration risk detected</strong>
            <p>
              {busFactorWarnings.map((w) => (
                <span key={w.employeeId} className="bus-factor-item">
                  {w.employeeName} has {w.roleCount} roles assigned
                </span>
              ))}
              {' '}— consider redistributing responsibilities to reduce single points of failure.
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="topology-filters">
        <div className="filter-group">
          <label htmlFor="role-filter">Filter by Role:</label>
          <select id="role-filter" value={filterRole} onChange={(e) => setFilterRole(e.target.value)}>
            {roles.map((role) => (
              <option key={role} value={role}>{role === 'all' ? 'All Roles' : role}</option>
            ))}
          </select>
        </div>
        <label className="checkbox-label">
          <input type="checkbox" checked={showBaseline} onChange={(e) => setShowBaseline(e.target.checked)} />
          Show only baseline personas
        </label>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="topology-loading" data-testid="topology-loading">
          Loading personas…
        </div>
      )}

      {/* Error State */}
      {!loading && error && (
        <div className="topology-error" role="alert">
          <p>Failed to load personas: {error}</p>
          <button className="pt-btn secondary" onClick={onRetry}>Retry</button>
        </div>
      )}

      {/* Empty State — org not seeded yet */}
      {!loading && !error && personas.length === 0 && (
        <div className="topology-empty" data-testid="topology-empty">
          <p className="empty-title">No FAIR personas seeded</p>
          <p>
            Run <code>deploy/seed-personas.sh</code> to seed the 30 Pistoia FAIR Maturity Matrix
            archetypes, or create a custom persona in the catalog.
          </p>
          <button className="pt-btn primary" onClick={onGoToCatalog}>Go to Catalog</button>
        </div>
      )}

      {/* Graph */}
      {!loading && !error && personas.length > 0 && (
        <div className={`topology-main ${draggedEmployee ? 'drag-active' : ''}`}>
          <EmployeeSidebar
            employees={employees}
            loading={employeesLoading}
            onDragStart={handleEmployeeDragStart}
            employeeAssignments={employeeAssignments}
            onUnassign={handleUnassign}
          />

          <div
            className={`graph-container ${isGraphFocused ? 'keyboard-focused' : ''}`}
            ref={containerRef}
            tabIndex={0}
            role="application"
            aria-label="FAIR persona topology graph. Use arrow keys to navigate between personas, Enter to select, Escape to close panels."
            aria-roledescription="Interactive force-directed graph"
            onKeyDown={handleGraphKeyDown}
            onFocus={handleGraphFocus}
            onBlur={handleGraphBlur}
            onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; }}
            onDragEnd={handleDragEnd}
          >
            <svg
              ref={svgRef}
              className="topology-svg"
              role="img"
              aria-label={`Graph showing ${filteredPersonas.length} governance personas`}
            />
            <TopologyLegend />
            {draggedEmployee && (
              <div className="drop-overlay">
                <div className="drop-hint">Drop on a persona node to assign {draggedEmployee.display_name}</div>
              </div>
            )}
            {filteredPersonas.length === 0 && (
              <div className="no-results-state">
                <p>No personas match your current filters.</p>
                <button className="pt-btn secondary" onClick={() => { setFilterRole('all'); setShowBaseline(false); }}>
                  Clear Filters
                </button>
              </div>
            )}
          </div>

          {selectedPersona && (
            <aside className="topology-detail" data-testid="topology-detail-panel">
              <div className="topology-detail-actions">
                <button
                  className="pt-btn primary"
                  onClick={() => handleAssignClick(selectedPersona)}
                  data-testid="topology-assign-button"
                >
                  Assign team member
                </button>
                <button
                  className="pt-btn secondary"
                  onClick={() => setSelectedPersona(null)}
                  aria-label="Close detail panel"
                >
                  Close
                </button>
              </div>
              {assignmentMap.has(selectedPersona.id) && (
                <p className="topology-detail-assignment">
                  Assigned to <strong>{assignmentMap.get(selectedPersona.id)?.employee_name}</strong>
                </p>
              )}
              {renderPersonaDetail(selectedPersona)}
            </aside>
          )}
        </div>
      )}

      {/* Drop Feedback Toast */}
      {dropFeedback && (
        <div className={`drop-feedback-toast ${dropFeedback.type}`}>
          <span className="toast-message">{dropFeedback.message}</span>
        </div>
      )}

      <AssignmentModal
        isOpen={assignModalOpen}
        persona={personaToAssign}
        onClose={() => { setAssignModalOpen(false); setPersonaToAssign(null); }}
        onAssign={handleAssign}
        authFetch={authFetch}
      />
      <AlertHistoryPanel isOpen={alertPanelOpen} onClose={() => setAlertPanelOpen(false)} authFetch={authFetch} />

      {/* DC-189: Screen reader announcement region */}
      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">{announceMessage}</div>
    </div>
  );
};

export default PersonaTopologyView;
